import React, { useState } from "react";
import axios from "axios";
import { Reveal } from "@/components/motion/Reveal";
import { Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { CONTACT_ENDPOINT } from "@/config";

export default function Contact() {
  const { t, site } = useI18n();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(c.errFill);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(CONTACT_ENDPOINT, form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success(c.success);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : c.errSend);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative bg-canvas-surface py-14 md:py-20"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-12 gap-8 md:gap-16">
        <div className="col-span-12 md:col-span-5">
          <Reveal>
            <p className="overline mb-6">{c.overline}</p>
            <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-ink">
              {c.headingA} <br />
              <span className="italic text-brand-red/80">{c.headingB}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-ink-muted text-base md:text-lg leading-relaxed max-w-md">
              {c.intro}
            </p>
          </Reveal>

          <div className="mt-12 space-y-6">
            <Reveal delay={0.2}>
              <a
                href={`mailto:${site.contact.email}`}
                data-testid="contact-email"
                className="flex items-start gap-4 group"
              >
                <Mail size={18} strokeWidth={1.2} className="text-brand-red mt-1" />
                <div>
                  <p className="overline mb-1">{c.emailLabel}</p>
                  <p className="serif text-2xl text-ink group-hover:text-brand-red transition-colors">
                    {site.contact.email}
                  </p>
                </div>
              </a>
            </Reveal>
            <Reveal delay={0.25}>
              <a
                href={`tel:${site.contact.phone.replace(/\s/g, "")}`}
                data-testid="contact-phone"
                className="flex items-start gap-4 group"
              >
                <Phone size={18} strokeWidth={1.2} className="text-brand-red mt-1" />
                <div>
                  <p className="overline mb-1">{c.phoneLabel}</p>
                  <p className="serif text-2xl text-ink group-hover:text-brand-red transition-colors">
                    {site.contact.phone}
                  </p>
                </div>
              </a>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="flex items-start gap-4">
                <MapPin size={18} strokeWidth={1.2} className="text-brand-red mt-1" />
                <div>
                  <p className="overline mb-1">{c.locationLabel}</p>
                  <p className="serif text-2xl text-ink">{site.location}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="col-span-12 md:col-span-7">
          <Reveal>
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="bg-canvas-surface border border-ink/10 p-8 md:p-12 space-y-8"
            >
              {sent && (
                <div className="flex items-center gap-3 text-brand-red border-l-2 border-brand-red/60 pl-4 py-2">
                  <CheckCircle2 size={18} strokeWidth={1.2} />
                  <p className="serif italic text-lg">
                    {c.sentInline}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="overline block mb-3" htmlFor="name">{c.formName} <span className="text-brand-red">*</span></label>
                  <input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    data-testid="contact-input-name"
                    className="field-underline"
                    placeholder={c.phName}
                  />
                </div>
                <div>
                  <label className="overline block mb-3" htmlFor="email">{c.formEmail} <span className="text-brand-red">*</span></label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    data-testid="contact-input-email"
                    className="field-underline"
                    placeholder={c.phEmail}
                  />
                </div>
              </div>

              <div>
                <label className="overline block mb-3" htmlFor="phone">{c.formPhone} <span className="text-brand-red">*</span></label>
                <input
                  id="phone"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  required
                  data-testid="contact-input-phone"
                  className="field-underline"
                  placeholder={c.phPhone}
                />
              </div>

              <div>
                <label className="overline block mb-3" htmlFor="message">{c.formMessage} <span className="text-brand-red">*</span></label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={5}
                  data-testid="contact-input-message"
                  className="field-underline resize-none"
                  placeholder={c.phMessage}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
                <p className="text-ink-muted text-xs tracking-wide max-w-xs">
                  {c.consent}
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-form-submit"
                  className="btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? c.submitting : c.submit}
                </button>
              </div>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="mt-10">
            <div className="aspect-[16/9] overflow-hidden border border-ink/10">
              <iframe
                title={c.mapTitle}
                src={site.mapEmbed}
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(0.25) contrast(0.95)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
